import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CatalogFeature } from "@/types/catalog-feature";
import { featureBadgeDisplayLabel } from "../utils/featureBadgeLabel";

type PlanFeaturesBadgesFieldLabels = {
  section: string;
  addPlaceholder: string;
  removeFeature: string;
  enabled: string;
  value: string;
};

type PlanFeaturesBadgesFieldProps = {
  featureOptions: CatalogFeature[];
  selectedFeatureIds: string[];
  onSelectedFeatureIdsChange: (ids: string[]) => void;
  featuresLoading: boolean;
  labels: PlanFeaturesBadgesFieldLabels;
  featureValueById?: Readonly<Partial<Record<string, string>>>;
  onFeatureValueByIdChange?: (valueById: Record<string, string>) => void;
};

function normalizeFeatureValue(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
}

export const PlanFeaturesBadgesField: React.FC<
  PlanFeaturesBadgesFieldProps
> = ({
  featureOptions,
  selectedFeatureIds,
  onSelectedFeatureIdsChange,
  featuresLoading,
  labels,
  featureValueById,
  onFeatureValueByIdChange,
}) => {
  const [addSelectKey, setAddSelectKey] = useState(0);

  const featureById = useMemo(() => {
    const m = new Map<string, CatalogFeature>();
    for (const f of featureOptions) {
      m.set(f.id, f);
    }
    return m;
  }, [featureOptions]);

  const availableToAdd = useMemo(
    () => featureOptions.filter((f) => !selectedFeatureIds.includes(f.id)),
    [featureOptions, selectedFeatureIds],
  );

  const removeFeature = (featureId: string) => {
    onSelectedFeatureIdsChange(
      selectedFeatureIds.filter((id) => id !== featureId),
    );
    if (!onFeatureValueByIdChange) {
      return;
    }

    const nextValues = { ...(featureValueById ?? {}) };
    delete nextValues[featureId];
    onFeatureValueByIdChange(nextValues);
  };

  const addFeature = (featureId: string) => {
    if (selectedFeatureIds.includes(featureId)) {
      return;
    }

    const feature = featureById.get(featureId);
    onSelectedFeatureIdsChange([...selectedFeatureIds, featureId]);

    if (onFeatureValueByIdChange) {
      const nextValues = { ...(featureValueById ?? {}) };
      nextValues[featureId] =
        feature?.type === "NUMBER"
          ? normalizeFeatureValue(feature.limit)
          : "true";
      onFeatureValueByIdChange(nextValues);
    }

    setAddSelectKey((k) => k + 1);
  };

  const updateFeatureValue = (featureId: string, value: string) => {
    if (!onFeatureValueByIdChange) {
      return;
    }

    onFeatureValueByIdChange({
      ...(featureValueById ?? {}),
      [featureId]: value,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-foreground text-sm font-medium">
          {labels.section}
        </span>
        <Select
          key={addSelectKey}
          disabled={featuresLoading || availableToAdd.length === 0}
          onValueChange={(v) => {
            addFeature(v);
          }}
        >
          <SelectTrigger className="w-[min(100%,220px)]">
            <SelectValue placeholder={labels.addPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {availableToAdd.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {featureBadgeDisplayLabel(f.id, f, featureValueById)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex min-h-8 flex-col gap-3">
        {selectedFeatureIds.map((id) => {
          const meta = featureById.get(id);
          const label = featureBadgeDisplayLabel(id, meta, featureValueById);
          const isNumberFeature = meta?.type === "NUMBER";
          const value =
            featureValueById?.[id] ??
            (isNumberFeature ? normalizeFeatureValue(meta?.limit) : "true");
          const enabled = value !== "false";

          return (
            <div
              key={id}
              className="border-base-border rounded-lg border bg-white p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-foreground truncate text-sm font-medium">
                    {label}
                  </span>
                  <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <span>{labels.enabled}</span>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(checked) =>
                        updateFeatureValue(id, checked ? "true" : "false")
                      }
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => removeFeature(id)}
                  aria-label={`${labels.removeFeature}: ${label}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {isNumberFeature ? (
                <div className="mt-3">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min={0}
                    label={labels.value}
                    value={value}
                    onChange={(event) =>
                      updateFeatureValue(id, event.target.value)
                    }
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
